<template>
  <section id="home" class="relative min-h-[600px] flex items-center justify-center overflow-hidden">
    <!-- Background layers -->
    <div class="absolute inset-0">
      <img src="@/assets/site-cover.jpg" alt="Cover" class="w-full h-full object-cover" />
      <div class="absolute inset-0 transition-all duration-500"
        :class="isDark
          ? 'bg-gradient-to-b from-space-900/90 via-galaxy-900/80 to-space-900/95'
          : 'bg-gradient-to-b from-primary-800/80 via-primary-700/70 to-primary-900/85'
        "
      ></div>
    </div>

    <!-- Stars overlay (dark mode) -->
    <div v-if="isDark" class="absolute inset-0 stars-bg opacity-60"></div>

    <!-- Nebula glow effects -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full blur-3xl opacity-20"
        :class="isDark ? 'bg-nebula-purple' : 'bg-primary-400'"
      ></div>
      <div class="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 rounded-full blur-3xl opacity-15"
        :class="isDark ? 'bg-nebula-pink' : 'bg-accent-400'"
      ></div>
      <div class="absolute top-1/3 right-1/4 w-1/3 h-1/3 rounded-full blur-3xl opacity-10"
        :class="isDark ? 'bg-nebula-cyan' : 'bg-secondary-300'"
      ></div>
    </div>

    <!-- Content -->
    <div class="relative z-10 text-center px-4 max-w-4xl mx-auto">
      <!-- Bugsy mascot — main character -->
      <div class="relative w-48 h-48 mx-auto mb-6 cursor-pointer select-none group"
        @click="bounce = true"
        @animationend="bounce = false"
      >
        <!-- Glow ring behind Bugsy -->
        <div class="absolute inset-0 rounded-full blur-2xl opacity-40 transition-opacity duration-300 group-hover:opacity-70"
          :class="isDark ? 'bg-nebula-purple' : 'bg-primary-400'"
        ></div>
        <!-- Sparkle dots -->
        <span class="absolute top-2 right-4 w-2.5 h-2.5 rounded-full bg-star-gold animate-ping opacity-75"></span>
        <span class="absolute bottom-6 left-3 w-2 h-2 rounded-full bg-nebula-cyan animate-ping opacity-60" style="animation-delay:0.5s"></span>
        <span class="absolute top-8 left-1 w-1.5 h-1.5 rounded-full bg-nebula-pink animate-ping opacity-50" style="animation-delay:1s"></span>
        <img
          src="@/assets/bugsy.png"
          alt="Bugsy — Starbugs Academy mascot"
          class="relative w-full h-full object-contain drop-shadow-2xl transition-transform duration-300 group-hover:scale-110"
          :class="[
            bounce ? 'bugsy-bounce' : 'animate-float',
            isDark ? 'drop-shadow-[0_0_24px_rgba(124,77,255,0.5)]' : 'drop-shadow-[0_0_20px_rgba(124,58,237,0.3)]'
          ]"
        />
      </div>
      <h1 class="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight drop-shadow-lg">
        {{ t('hero.title') }}
      </h1>
      <p class="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
        {{ t('hero.subtitle') }}
      </p>
      <a href="#register"
        class="inline-block galaxy-btn-primary text-lg"
      >
        {{ t('hero.cta') }}
      </a>
    </div>
  </section>
</template>

<style scoped>
@keyframes bugsy-jump {
  0%, 100% { transform: translateY(0) scale(1); animation-timing-function: cubic-bezier(0.8, 0, 1, 1); }
  45%       { transform: translateY(-30%) scale(1.05); animation-timing-function: cubic-bezier(0, 0, 0.2, 1); }
}

.bugsy-bounce {
  animation: bugsy-jump 0.45s ease 2 forwards;
}
</style>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ isDark: boolean }>()

const { t } = useI18n()
const bounce = ref(false)
</script>
